// Web-exclusive EditEducation screen
// CRUD operations for education entries

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, GraduationCap, ChevronDown, ChevronUp } from 'lucide-react';
import WebLayout from '../../../components/web/WebLayout';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../../constants/webStyles';
import {
  getEducation,
  postEducation,
  updateEducation,
  deleteEducation,
} from '../../../services/profile';
import { useIsMobile } from '../../../hooks/useIsMobile';

const EditEducationScreen = () => {
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    school: '',
    year: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [errorFields, setErrorFields] = useState([]);
  const [showYearPicker, setShowYearPicker] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);

  // Generate years array (last 35 years)
  const years = Array.from({ length: 35 }, (_, k) => {
    const year = new Date().getFullYear() - k;
    return { label: year.toString(), value: year.toString() };
  });

  // Fetch education data
  const { data: educationQuery, isLoading } = useQuery({
    queryKey: ['education'],
    queryFn: getEducation,
  });
  const educationList = educationQuery?.data?.data || [];

  // Toast notification helper
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Add education mutation
  const addMutation = useMutation({
    mutationFn: postEducation,
    onSuccess: () => {
      queryClient.invalidateQueries(['education']);
      resetForm();
      showToast('Education added successfully!', 'success');
    },
    onError: (err) => {
      showToast(err?.response?.data?.message || 'Failed to add education', 'error');
    },
  });

  // Update education mutation
  const updateMutation = useMutation({
    mutationFn: updateEducation,
    onSuccess: () => {
      queryClient.invalidateQueries(['education']);
      setEditingId(null);
      resetForm();
      showToast('Education updated successfully!', 'success');
    },
    onError: (err) => {
      showToast(err?.response?.data?.message || 'Failed to update education', 'error');
    },
  });

  // Delete education mutation
  const deleteMutation = useMutation({
    mutationFn: deleteEducation,
    onSuccess: () => {
      queryClient.invalidateQueries(['education']);
      showToast('Education deleted successfully!', 'success');
    },
    onError: (err) => {
      showToast(err?.response?.data?.message || 'Failed to delete education', 'error');
    },
  });

  // Form validation
  const validateForm = () => {
    const errors = [];
    if (!formData.name?.trim()) errors.push('name');
    if (!formData.school?.trim()) errors.push('school');
    if (!formData.year) errors.push('year');
    setErrorFields(errors);
    return errors.length === 0;
  };

  // Update form field
  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errorFields.includes(field)) {
      setErrorFields((prev) => prev.filter((f) => f !== field));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ name: '', school: '', year: '' });
    setErrorFields([]);
    setShowYearPicker(false);
  };

  // Handle save (add or update)
  const handleSave = () => {
    if (!validateForm()) return;

    const payload = {
      name: formData.name.trim(),
      school: formData.school.trim(),
      year: formData.year,
    };

    if (editingId) {
      updateMutation.mutate({ uuid: editingId, ...payload });
    } else {
      addMutation.mutate(payload);
    }
  };

  // Handle edit
  const handleEdit = (education) => {
    setEditingId(education.uuid);
    setFormData({
      name: education.name,
      school: education.school,
      year: education.year,
    });
    setErrorFields([]);
    setShowYearPicker(false);
  };

  // Handle delete with confirmation
  const handleDelete = (education) => {
    if (window.confirm('Are you sure you want to delete this education?')) {
      deleteMutation.mutate({ uuid: education.uuid });
    }
  };

  // Handle cancel edit
  const handleCancel = () => {
    setEditingId(null);
    resetForm();
  };

  // Handle back navigation
  const handleBack = () => {
    navigation.goBack();
  };

  // Loading state
  if (isLoading) {
    return (
      <WebLayout>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={webColors.primary} />
          <Text style={styles.loadingText}>Loading education...</Text>
        </View>
      </WebLayout>
    );
  }

  const isSaving = addMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <WebLayout>
      {/* Toast Notification */}
      {toast && (
        <View
          style={[
            styles.toast,
            toast.type === 'success' && styles.toastSuccess,
            toast.type === 'error' && styles.toastError,
          ]}
        >
          <Text style={styles.toastText}>{toast.message}</Text>
        </View>
      )}

      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <TouchableOpacity style={styles.backButton} onPress={handleBack}>
            <View style={styles.backButtonContent}>
              <ArrowLeft size={16} color={webColors.textSecondary} />
              <Text style={styles.backButtonText}>Back to Profile</Text>
            </View>
          </TouchableOpacity>
          <Text style={[styles.pageTitle, isMobile && styles.pageTitleMobile]}>Edit Education</Text>
          <Text style={styles.pageSubtitle}>
            Add relevant qualifications including degrees, diplomas, and other certifications.
          </Text>
        </View>

        {/* Content Card */}
        <View style={styles.contentCard}>
          {/* Education List */}
          {educationList.length > 0 && (
            <View style={styles.educationList}>
              <Text style={styles.sectionTitle}>Your Education</Text>
              {educationList.map((education, index) => (
                <View key={education.uuid} style={styles.educationItem}>
                  {editingId === education.uuid ? (
                    // Edit Mode
                    <View style={styles.editFormContainer}>
                      <EducationForm
                        formData={formData}
                        updateField={updateField}
                        errorFields={errorFields}
                        years={years}
                        showYearPicker={showYearPicker}
                        setShowYearPicker={setShowYearPicker}
                      />
                      <View style={styles.editActions}>
                        <TouchableOpacity style={styles.cancelBtn} onPress={handleCancel}>
                          <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.saveBtn, isSaving && styles.btnDisabled]}
                          onPress={handleSave}
                          disabled={isSaving}
                        >
                          <Text style={styles.saveBtnText}>
                            {isSaving ? 'Saving...' : 'Save'}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  ) : (
                    // Display Mode
                    <View style={styles.educationDisplayRow}>
                      <View style={styles.educationIcon}>
                        <GraduationCap size={24} color="#92400e" />
                      </View>
                      <View style={styles.educationInfo}>
                        <Text style={styles.educationDegree}>{education.name}</Text>
                        <Text style={styles.educationSchool}>{education.school}</Text>
                        <Text style={styles.educationYear}>{education.year}</Text>
                      </View>
                      <View style={styles.educationActions}>
                        <TouchableOpacity
                          style={styles.actionBtn}
                          onPress={() => handleEdit(education)}
                        >
                          <Text style={styles.actionBtnText}>Edit</Text>
                        </TouchableOpacity>
                        {/* First entry cannot be deleted */}
                        {index !== 0 && (
                          <TouchableOpacity
                            style={[styles.actionBtn, styles.deleteBtn]}
                            onPress={() => handleDelete(education)}
                          >
                            <Text style={styles.deleteBtnText}>Delete</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    </View>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* Add New Form (shown when not editing) */}
          {editingId === null && (
            <View style={styles.addSection}>
              <Text style={styles.sectionTitle}>
                {educationList.length > 0 ? 'Add Another Education' : 'Add Education'}
              </Text>
              <EducationForm
                formData={formData}
                updateField={updateField}
                errorFields={errorFields}
                years={years}
                showYearPicker={showYearPicker}
                setShowYearPicker={setShowYearPicker}
              />
              <TouchableOpacity
                style={[styles.addButton, isSaving && styles.btnDisabled]}
                onPress={handleSave}
                disabled={isSaving}
              >
                <Text style={styles.addButtonText}>
                  {isSaving ? 'Adding...' : 'Add Education'}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        <View style={{ height: 60 }} />
      </ScrollView>
    </WebLayout>
  );
};

// Education Form Component
const EducationForm = ({
  formData,
  updateField,
  errorFields,
  years,
  showYearPicker,
  setShowYearPicker,
}) => {
  const isMobile = useIsMobile();

  return (
    <View style={styles.formGrid}>
      {/* Degree Field - Full Width */}
      <View style={styles.formGroupFullWidth}>
        <Text style={styles.formLabel}>
          Degree / Qualification <Text style={styles.requiredStar}>*</Text>
        </Text>
        <TextInput
          style={[styles.formInput, errorFields.includes('name') && styles.formInputError]}
          value={formData.name}
          onChangeText={(v) => updateField('name', v)}
          placeholder="e.g., Bachelor of Medicine"
          placeholderTextColor={webColors.textMuted}
        />
      </View>

      {/* School Field */}
      <View style={[styles.formGroup, isMobile && styles.formGroupMobile]}>
        <Text style={styles.formLabel}>
          University / College <Text style={styles.requiredStar}>*</Text>
        </Text>
        <TextInput
          style={[styles.formInput, errorFields.includes('school') && styles.formInputError]}
          value={formData.school}
          onChangeText={(v) => updateField('school', v)}
          placeholder="e.g., University of Malaya"
          placeholderTextColor={webColors.textMuted}
        />
      </View>

      {/* Year Dropdown */}
      <View style={[styles.formGroup, { zIndex: 1000 }, isMobile && styles.formGroupMobile]}>
        <Text style={styles.formLabel}>
          Year <Text style={styles.requiredStar}>*</Text>
        </Text>
        <TouchableOpacity
          style={[styles.selectDropdown, errorFields.includes('year') && styles.formInputError]}
          onPress={() => setShowYearPicker(!showYearPicker)}
        >
          <Text
            style={formData.year ? styles.selectDropdownText : styles.selectDropdownPlaceholder}
          >
            {formData.year || 'Select year'}
          </Text>
          <View style={styles.selectDropdownArrow}>
            {showYearPicker ? (
              <ChevronUp size={16} color={webColors.textMuted} />
            ) : (
              <ChevronDown size={16} color={webColors.textMuted} />
            )}
          </View>
        </TouchableOpacity>
        {showYearPicker && (
          <View style={styles.yearPickerDropdown}>
            <ScrollView style={styles.yearPickerScroll} nestedScrollEnabled>
              {years.map((year) => (
                <TouchableOpacity
                  key={year.value}
                  style={[
                    styles.yearOption,
                    formData.year === year.value && styles.yearOptionSelected,
                  ]}
                  onPress={() => {
                    updateField('year', year.value);
                    setShowYearPicker(false);
                  }}
                >
                  <Text
                    style={[
                      styles.yearOptionText,
                      formData.year === year.value && styles.yearOptionTextSelected,
                    ]}
                  >
                    {year.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 100,
  },
  loadingText: {
    fontSize: 16,
    color: webColors.textMuted,
    marginTop: 12,
  },

  // Toast
  toast: {
    position: 'absolute',
    top: 80,
    right: 20,
    backgroundColor: webColors.cardBackground,
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: webBorderRadius.small,
    ...webShadows.modal,
    zIndex: 1000,
    minWidth: 300,
    borderLeftWidth: 4,
    borderLeftColor: webColors.info,
  },
  toastSuccess: {
    borderLeftColor: webColors.success,
  },
  toastError: {
    borderLeftColor: webColors.error,
  },
  toastText: {
    fontSize: 14,
    color: webColors.textPrimary,
  },

  // Header
  pageHeader: {
    marginBottom: 24,
  },
  backButton: {
    marginBottom: 16,
  },
  backButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  backButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  pageSubtitle: {
    fontSize: 16,
    color: webColors.textSecondary,
  },

  // Content Card
  contentCard: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.large,
    padding: webSpacing.cardPadding,
    ...webShadows.card,
  },

  // Section Title
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
  },

  // Education List
  educationList: {
    marginBottom: 32,
  },
  educationItem: {
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
    paddingVertical: 16,
  },
  educationDisplayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  educationIcon: {
    width: 48,
    height: 48,
    borderRadius: webBorderRadius.small,
    backgroundColor: '#fef3c7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  educationInfo: {
    flex: 1,
  },
  educationDegree: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  educationSchool: {
    fontSize: 14,
    color: webColors.textSecondary,
    marginBottom: 2,
  },
  educationYear: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  educationActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionBtn: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 6,
  },
  actionBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.primary,
  },
  deleteBtn: {
    backgroundColor: webColors.errorLight,
  },
  deleteBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.error,
  },

  // Edit Form Container
  editFormContainer: {
    paddingTop: 8,
  },
  editActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 16,
  },
  cancelBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.small,
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  saveBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: webColors.success,
    borderRadius: webBorderRadius.small,
  },
  saveBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  btnDisabled: {
    opacity: 0.6,
  },

  // Add Section
  addSection: {
    marginTop: 8,
  },

  // Form Grid
  formGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  formGroup: {
    width: Platform.OS === 'web' ? 'calc(50% - 10px)' : '48%',
    position: 'relative',
  },
  formGroupFullWidth: {
    width: '100%',
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
    marginBottom: 8,
  },
  requiredStar: {
    color: webColors.error,
  },
  formInput: {
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 12,
    paddingHorizontal: 16,
    fontSize: 15,
    color: webColors.textPrimary,
    backgroundColor: webColors.cardBackground,
  },
  formInputError: {
    borderColor: webColors.error,
    borderWidth: 2,
  },

  // Year Dropdown
  selectDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  selectDropdownText: {
    fontSize: 15,
    color: webColors.textPrimary,
  },
  selectDropdownPlaceholder: {
    fontSize: 15,
    color: webColors.textMuted,
  },
  selectDropdownArrow: {},
  yearPickerDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    marginTop: 4,
    zIndex: 1000,
    ...webShadows.modal,
  },
  yearPickerScroll: {
    maxHeight: 200,
  },
  yearOption: {
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  yearOptionSelected: {
    backgroundColor: webColors.primaryLight,
  },
  yearOptionText: {
    fontSize: 15,
    color: webColors.textPrimary,
  },
  yearOptionTextSelected: {
    color: webColors.primaryDark,
    fontWeight: '600',
  },

  // Add Button
  addButton: {
    backgroundColor: webColors.primary,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginTop: 20,
    ...webShadows.button,
  },
  addButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },

  // Mobile variants
  formGroupMobile: {
    width: '100%',
  },
  pageTitleMobile: {
    fontSize: 22,
  },
});

export default EditEducationScreen;
